import dayjs from 'dayjs'

import { selectExperiment } from '@/redux/slices/statsig'
import { getHydratedStore } from '@/redux/store/configureStore'

import { EXPLORE_ALLOWED_EMAILS, EXPLORE_CT_EXPERIMENT } from '@/constants/explore'
import { isServer } from '@/utils/general'
import { isEmpty } from '@/utils/general/browserHelper'
import { getCookie, setCookie } from '@/utils/general/cookie'
import { getFormattedDate } from '@/utils/general/date'
import { getUserAuthValues } from '@/utils/general/user'

const EXPLORE_DEFAULT_SOURCE = 'BLR'
const FHP_DATE_FORMAT = 'DD/MM/YYYY'
const EXPLORE_DATE_FORMAT = 'YYYY-MM-DD'

/**
 * Explore gate: email allow-list OR Statsig/cookie `test` variant.
 * Mirrors `isWowPricingEnabled` order of checks.
 */
export function isExploreFeatureEnabled(exploreVariant = ''): boolean {
    if (isServer()) {
        return exploreVariant?.toLowerCase() === EXPLORE_CT_EXPERIMENT.VARIANTS.TEST
    }

    const usermisc = getCookie('usermisc') || ''
    const { email: signedInEmail = '' } = getUserAuthValues() || {}
    if (
        usermisc.includes('SIGNED_IN') &&
        !isEmpty(signedInEmail) &&
        (EXPLORE_ALLOWED_EMAILS as readonly string[]).includes(signedInEmail.toLowerCase())
    ) {
        setCookie({
            name: EXPLORE_CT_EXPERIMENT.COOKIE,
            value: EXPLORE_CT_EXPERIMENT.VARIANTS.TEST,
            expiryInDay: 365,
        })
        return true
    }

    const cookieValue = getCookie(EXPLORE_CT_EXPERIMENT.COOKIE)

    return (
        exploreVariant?.toLowerCase() === EXPLORE_CT_EXPERIMENT.VARIANTS.TEST ||
        cookieValue?.toLowerCase() === EXPLORE_CT_EXPERIMENT.VARIANTS.TEST
    )
}

/** Explore AB variant for analytics: `explore_ct` override cookie wins over the SSR-hydrated Redux value; `control` if neither. */
export function getExploreAbVariant(): string {
    let reduxVariant = ''
    try {
        reduxVariant = selectExperiment(EXPLORE_CT_EXPERIMENT.NAME)(getHydratedStore().getState())
    } catch {
        reduxVariant = ''
    }
    return getCookie(EXPLORE_CT_EXPERIMENT.COOKIE) || reduxVariant || EXPLORE_CT_EXPERIMENT.VARIANTS.CONTROL
}

/** FHP stores depart date as DD/MM/YYYY; explore expects YYYY-MM-DD. */
export function toExploreDateParam(departDateDdMmYyyy?: string): string {
    const fallback = getFormattedDate(dayjs().add(2, 'day').toDate(), EXPLORE_DATE_FORMAT)
    if (isEmpty(departDateDdMmYyyy) || departDateDdMmYyyy === 'Invalid Date') {
        return fallback
    }

    const formatted = getFormattedDate(departDateDdMmYyyy, EXPLORE_DATE_FORMAT, FHP_DATE_FORMAT)
    if (isEmpty(formatted) || formatted.toLowerCase() === 'invalid date') {
        return fallback
    }

    return formatted
}

export function getExploreRouteQuery({
    source,
    departDate,
}: {
    source?: string
    departDate?: string
}): { source: string; date: string } {
    const code = source?.trim().toUpperCase()
    const validSource = code && /^[A-Z]{3}$/.test(code)

    return {
        source: validSource ? code : EXPLORE_DEFAULT_SOURCE,
        date: toExploreDateParam(departDate),
    }
}
